import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { animated, useSprings } from "react-spring";
import { useSwipeable } from "react-swipeable";
import CaptainCard from "../components/captain/CaptainCard/CaptainCard";
import PositionSlider from "../components/layouts/PositionSlider/PositionSlider";
import { useCaptains } from "../hooks/useCaptains";

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const CaptainListPage: React.FC = () => {
    const { captainName } = useParams<{ captainName: string; }>();
    const { data: captains, isLoading } = useCaptains();
    const [initialIndex, setInitialIndex] = useState(0);

    useEffect(() => {
        if (captainName && captains.length > 0) {
            const foundIndex = captains.findIndex(
                (captain) => captain.route === `/${captainName}`
            );
            if (foundIndex !== -1) {
                setInitialIndex(foundIndex);
                window.history.replaceState({}, "", `/${captainName}`);
            }
        }
    }, [captainName, captains]);

    const retainIndex = captains.findIndex(
        (captain) => captain.route === `/${captainName}`
    );
    const [shouldAnimate, setShouldAnimate] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(
        retainIndex !== -1 ? retainIndex : initialIndex
    );

    useEffect(() => {
        if (captains.length === 0) return;

        if (captainName) {
            const captainIndex = captains.findIndex(
                (captain) =>
                    encodeURIComponent(captain.route).replace("%2F", "") ===
                    captainName
            );
            if (captainIndex !== -1) {
                setCurrentIndex(captainIndex);
            }
        } else {
            setCurrentIndex(initialIndex);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [captains]);

    const springs = useSprings(
        captains.length,
        captains.map((_, i) => ({
            y: `${(i - currentIndex) * 100}vh`,
            config: shouldAnimate
                ? { tension: 250, friction: 30 }
                : { tension: 0, friction: 0 },
        }))
    );

    const handlers = useSwipeable({
        onSwipedUp: () => {
            setShouldAnimate(currentIndex !== captains.length - 1);
            setCurrentIndex((prev) =>
                prev === captains.length - 1
                    ? 0
                    : Math.min(prev + 1, captains.length - 1)
            );
        },
        onSwipedDown: () => {
            setShouldAnimate(currentIndex !== 0);
            setCurrentIndex((prev) =>
                prev === 0 ? captains.length - 1 : Math.max(prev - 1, 0)
            );
        },
        delta: 50,
        preventScrollOnSwipe: isMobile,
        trackMouse: isMobile,
    });

    useEffect(() => {
        if (!isMobile && captains.length > 0) {
            const handleScroll = (event: WheelEvent) => {
                if (event.deltaY > 0) {
                    setCurrentIndex((prev) =>
                        prev === captains.length - 1
                            ? 0
                            : Math.min(prev + 1, captains.length - 1)
                    );
                    setShouldAnimate(currentIndex !== captains.length - 1);
                } else if (event.deltaY < 0) {
                    setShouldAnimate(currentIndex !== 0);
                    setCurrentIndex((prev) =>
                        prev === 0 ? captains.length - 1 : Math.max(prev - 1, 0)
                    );
                }
            };

            window.addEventListener("wheel", handleScroll);
            return () => {
                window.removeEventListener("wheel", handleScroll);
            };
        }
    }, [currentIndex, captains.length]);

    useEffect(() => {
        if (captains.length > 0 && captains[currentIndex]) {
            const currentCaptain = captains[currentIndex];
            const captainUrlName = encodeURIComponent(currentCaptain.route).replace(
                "%2F",
                ""
            );

            const url = window.location.href.split("/")[3];

            if (url !== captainUrlName) {
                window.history.pushState({}, "", `/${captainUrlName}`);
            }
        }
    }, [currentIndex, captains]);

    if (isLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", fontSize: "1.5rem" }}>
                Loading captains...
            </Box>
        );
    }

    return (
        <>
            <Box
                {...handlers}
                sx={{
                    height: "100vh",
                    overflow: "hidden",
                    position: "relative",
                    touchAction: "none",
                }}
            >
                {springs.map((style, index) => (
                    <animated.div
                        key={index}
                        style={{
                            cursor: "pointer",
                            transform: style.y.to(
                                (y) => `translate3d(0,${y},0)`
                            ),
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100vh",
                            willChange: "transform",
                        }}
                    >
                        <CaptainCard
                            captain={captains[index]}
                            triggerBanner={index === currentIndex}
                        />
                    </animated.div>
                ))}
            </Box>

            <PositionSlider
                captains={captains}
                currentIndex={currentIndex}
                onScrollToIndex={(index) =>
                    setCurrentIndex(
                        Math.max(0, Math.min(index, captains.length - 1))
                    )
                }
            />
        </>
    );
};

export default CaptainListPage;
